import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

import { BLOCK_SPACING } from '../fields/tokens';
import { selectField } from '../fields/widgets/select-field';

import { StyledFlexSlot } from './flex-layout';
import { starterChild } from './slot';
import type { SlotProps, StarterChild } from './slot';

export const HERO_CONTENT_GROUP_ALLOWED_COMPONENTS = ['Heading', 'Paragraph', 'Button', 'Image'];

const LAYOUT_OPTIONS = [
  { label: 'Row', value: 'row' },
  { label: 'Stack', value: 'column' },
] as const;

export type HeroContentGroupProps = {
  content?: React.FC<SlotProps>;
  desktopLayout: 'column' | 'row';
  mobileLayout: 'column' | 'row';
};

/** Row layouts center mixed-height children; stacked layouts keep every child full-width. */
const StyledHeroContentGroupSlot = styled(StyledFlexSlot)`
  align-items: var(--cms-hero-content-align-items, center);

  @media ${mediaQueries.largePhone} {
    align-items: var(--cms-hero-content-mobile-align-items, stretch);
  }
`;

function cloneValue<T>(value: T): T {
  if (Array.isArray(value)) return value.map(cloneValue) as T;
  if (!value || typeof value !== 'object') return value;

  return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, cloneValue(entry)])) as T;
}

/** Returns a fresh two-Button starting point without constraining what authors can keep in the group. */
export function getHeroContentGroupStarter(): StarterChild[] {
  return cloneValue([
    starterChild('Button', { label: 'Shop now', href: '/shop', buttonStyle: 'primary', align: 'center' }),
    starterChild('Button', {
      label: 'View specials',
      href: '/specials',
      buttonStyle: 'secondary',
      align: 'center',
    }),
  ]);
}

/**
 * Hero composition block. It stays in the drawer so authors can create or reinsert a group, while
 * AI cannot generate it standalone and its slot accepts only Hero-safe content.
 */
export const heroContentGroupConfig: ComponentConfig<HeroContentGroupProps> = {
  label: 'Hero content group',
  ai: { exclude: true },
  permissions: { drag: true, duplicate: true, delete: true },
  fields: {
    content: { type: 'slot', allow: HERO_CONTENT_GROUP_ALLOWED_COMPONENTS },
    desktopLayout: selectField(
      'Desktop layout',
      LAYOUT_OPTIONS,
      "Use 'row' to arrange this group's content side by side or 'column' to stack it."
    ),
    mobileLayout: selectField(
      'Mobile layout',
      LAYOUT_OPTIONS,
      "Use 'column' to stack this group's content on phones or 'row' to keep it side by side."
    ),
  },
  defaultProps: {
    content: (getHeroContentGroupStarter() as unknown) as React.FC<SlotProps>,
    desktopLayout: 'row',
    mobileLayout: 'column',
  },
  render: ({ content: slotContent, desktopLayout = 'row', mobileLayout = 'column' }) => {
    if (!slotContent) return <></>;
    const Slot = slotContent;
    const resolvedDesktopLayout = desktopLayout === 'column' ? 'column' : 'row';
    const resolvedMobileLayout = mobileLayout === 'row' ? 'row' : 'column';
    const alignItemsFor = (layout: 'column' | 'row'): 'center' | 'stretch' => (layout === 'row' ? 'center' : 'stretch');

    return (
      <Slot
        as={StyledHeroContentGroupSlot}
        allow={HERO_CONTENT_GROUP_ALLOWED_COMPONENTS}
        minEmptyHeight={48}
        style={
          {
            '--cms-flex-direction': resolvedDesktopLayout,
            '--cms-flex-mobile-direction': resolvedMobileLayout,
            '--cms-flex-item-flex': '0 1 auto',
            '--cms-flex-item-mobile-flex': '0 1 auto',
            '--cms-hero-content-align-items': alignItemsFor(resolvedDesktopLayout),
            '--cms-hero-content-mobile-align-items': alignItemsFor(resolvedMobileLayout),
            display: 'flex',
            flexWrap: 'wrap',
            gap: BLOCK_SPACING.sm,
            justifyContent: 'var(--cms-hero-content-justify, center)',
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          } as React.CSSProperties
        }
      />
    );
  },
};
